import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtAddress, fmtCurrency } from "@/lib/format";
import { AlertTriangle, CheckCircle2, Download, FileText, Loader2, Send, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import { ImpicDados } from "@/components/ImpicDados";
import { ImpicSubmetido } from "@/components/ImpicSubmetido";

export const card = "bg-white rounded-[16px] p-4 px-5 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0";
const btn = "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-[filter,background-color,opacity] disabled:opacity-40 disabled:cursor-not-allowed";
const NEG = { venda: "Venda", arrendamento: "Arrendamento" };

export function deadlineText(p) {
  if (p.status === "submetido") return `Prazo ${fmtDate(p.deadline)}`;
  if (!p.ended) return `Trimestre em curso · prazo ${fmtDate(p.deadline)}`;
  if (p.days_left < 0) return `Prazo ultrapassado a ${fmtDate(p.deadline)}`;
  return `Prazo ${fmtDate(p.deadline)} · ${p.days_left === 0 ? "hoje" : `faltam ${p.days_left} dias`}`;
}

export function StatusPill({ p, short }) {
  const tone = { por_validar: "bg-amber-50 text-amber-800 border-amber-200", validado: "bg-sky-50 text-sky-800 border-sky-200", submetido: "bg-green-50 text-green-800 border-green-200" }[p.status];
  const full = p.status === "validado" ? `Validado por ${p.validated_by} a ${fmtDate(p.validated_at)}`
    : p.status === "submetido" ? `Submetido a ${fmtDate(p.submitted_at)}` : "Por validar";
  return <span className={`text-[12px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${tone}`} data-testid={`impic-status-${p.key}${short ? "-short" : ""}`}>{short ? p.status_label : full}</span>;
}

export async function openPdf(url, name, download) {
  const win = download ? null : window.open("", "_blank");
  try {
    const res = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    if (win) win.location.href = href;
    else { const a = document.createElement("a"); a.href = href; a.download = name; a.click(); }
    return true;
  } catch (e) {
    if (win) win.close();
    const txt = e.response?.data instanceof Blob ? await e.response.data.text() : null;
    toast.error(apiErr(txt ? JSON.parse(txt).detail : null));
    return false;
  }
}

function Steps({ p }) {
  const idx = { por_validar: 0, validado: 1, submetido: 2 }[p.status];
  const lbl = ["Por validar", p.validated_by ? `Validado por ${p.validated_by} a ${fmtDate(p.validated_at)}` : "Validado", p.submitted_at ? `Submetido a ${fmtDate(p.submitted_at)}` : "Submetido"];
  return (
    <ol className="flex flex-wrap items-center gap-2 text-[13px]" data-testid="impic-steps">
      {lbl.map((l, i) => (
        <li key={i} className="flex items-center gap-2">
          <span className={`px-2.5 py-1 rounded-full border ${i < idx ? "bg-slate-50 text-slate-500 border-slate-200" : i === idx ? "navy-bg text-white border-transparent font-semibold" : "text-slate-400 border-dashed border-slate-300"}`}>{l}</span>
          {i < 2 && <span className="text-slate-300">→</span>}
        </li>
      ))}
    </ol>
  );
}

function groupMissing(missing) {
  const g = {};
  missing.forEach((m) => {
    const k = `${m.where || "process"}|${m.section.replace(/ \d+ de \d+$/, "")}|${m.reason}`;
    (g[k] = g[k] || { where: m.where || "process", section: m.section, reason: m.reason, fields: [] }).fields.push(m.field);
  });
  return Object.values(g);
}

function TxRow({ r, onEdit }) {
  const [open, setOpen] = useState(false);
  const n = r.missing.length;
  return (
    <li className="py-3.5" data-testid={`impic-tx-${r.ref}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/processo/${r.id}`} className="font-semibold navy-text hover:underline" data-testid={`impic-tx-link-${r.ref}`}>{r.ref}</Link>
          <span className="text-slate-600"> · {fmtAddress(r.address) || "sem morada"}</span>
          <div className="text-[13px] text-slate-500">{NEG[r.negocio]} · {fmtDate(r.date)}{r.price ? ` · ${fmtCurrency(r.price)}` : ""}{r.consultant ? ` · ${r.consultant}` : ""}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {n ? <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-[13px] font-semibold text-amber-800" data-testid={`impic-tx-missing-${r.ref}`}>
            <AlertTriangle size={14} /> {n} {n === 1 ? "campo em falta" : "campos em falta"} {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
            : <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-green-700" data-testid={`impic-tx-ok-${r.ref}`}><CheckCircle2 size={14} /> Completo</span>}
          <button onClick={() => onEdit(r)} className="text-[13px] underline text-slate-600 hover:text-slate-900" data-testid={`impic-tx-edit-${r.ref}`}>Pagamento</button>
          <button onClick={() => openPdf(`/impic/processes/${r.id}/anexo-b`, `ANEXO_B_${r.ref}.pdf`)} className="text-[13px] underline text-slate-600 hover:text-slate-900" data-testid={`impic-tx-pdf-${r.ref}`}>Ver Anexo B</button>
        </div>
      </div>
      {open && n > 0 && (
        <ul className="mt-2.5 space-y-1.5 rounded-lg bg-amber-50/60 border border-amber-100 px-3.5 py-2.5 text-[13px]" data-testid={`impic-tx-missing-list-${r.ref}`}>
          {groupMissing(r.missing).map((g, i) => (
            <li key={i} className="flex flex-wrap items-baseline justify-between gap-2">
              <span><b className="font-semibold">{g.section.replace(/ \d+ de \d+$/, "")}</b>: {g.fields.join(", ")} <span className="text-slate-500">· {g.reason}</span></span>
              {g.where === "impic"
                ? <button onClick={() => onEdit(r)} className="font-semibold navy-text underline" data-testid={`impic-tx-fill-${r.ref}-${i}`}>Preencher</button>
                : <Link to={`/processo/${r.id}`} className="font-semibold navy-text underline" data-testid={`impic-tx-open-${r.ref}-${i}`}>Abrir processo</Link>}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Actions({ p, busy, onValidate, onExport, onSubmit }) {
  const why = !p.ended ? "Pode validar depois de o trimestre terminar." : p.missing_count ? `Só pode validar com 0 campos em falta (faltam ${p.missing_count}).` : null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {p.status === "por_validar" && <button disabled={!!why || busy} onClick={onValidate} className={`${btn} navy-bg text-white hover:brightness-110`} data-testid="impic-validate-btn"><ShieldCheck size={16} /> Validar</button>}
        {p.status !== "por_validar" && p.count > 0 && <button disabled={busy} onClick={onExport} className={`${btn} ${p.status === "validado" ? "navy-bg text-white hover:brightness-110" : "border border-slate-300 navy-text hover:bg-slate-50"}`} data-testid="impic-export-btn"><Download size={16} /> Exportar</button>}
        {p.status === "validado" && <button disabled={busy} onClick={onSubmit} className={`${btn} border border-slate-300 navy-text hover:bg-slate-50`} data-testid="impic-submit-btn"><Send size={16} /> Marcar como submetido</button>}
      </div>
      {p.status === "por_validar" && why && <p className="text-[13px] text-slate-500" data-testid="impic-validate-why">{why}</p>}
    </div>
  );
}

export function ImpicPeriodo({ pkey, isDir, onChange }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [submit, setSubmit] = useState(false);
  const load = useCallback(() => api.get(`/impic/periods/${pkey}`).then(({ data }) => setP(data)), [pkey]);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setP(data); onChange(); toast.success(ok); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const exportar = async () => { setBusy(true); await openPdf(`/impic/periods/${pkey}/export`, `IMPIC_${pkey}.pdf`, true); setBusy(false); };
  if (!p) return <div className={`${card} py-16 grid place-items-center text-slate-400`}><Loader2 className="animate-spin" /></div>;
  return (
    <section className={`${card} p-5 sm:p-6 space-y-5`} data-testid="impic-period-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl navy-text" data-testid="impic-period-title">{p.label}</h2>
          <p className="text-sm text-slate-500">{fmtDate(p.start)} a {fmtDate(p.end)} · {deadlineText(p)}</p>
        </div>
        <StatusPill p={p} />
      </div>
      <Steps p={p} />
      {p.reopened && <p className="text-[13px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="impic-reopened">Houve alterações depois da validação. Reveja e valide outra vez.</p>}
      {p.changed_after_submit && <p className="text-[13px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="impic-changed">Há transações diferentes das que foram submetidas. Confirme no portal do IMPIC se é preciso uma comunicação de substituição.</p>}
      <Actions p={p} busy={busy} onValidate={() => run(() => api.post(`/impic/periods/${pkey}/validate`), "Período validado")} onExport={exportar} onSubmit={() => setSubmit(true)} />
      {p.agency_missing.length > 0 && (
        <div className="rounded-lg border border-amber-100 bg-amber-50/60 px-4 py-3 text-[13px]" data-testid="impic-agency-missing">
          <div className="font-semibold text-amber-900">Dados da agência em falta ({p.agency_missing.length})</div>
          <p className="text-slate-700 mt-0.5">{p.agency_missing.map((m) => `${m.section.slice(3)}: ${m.field}`).join(" · ")}</p>
          {isDir ? <Link to="/definicoes" className="inline-block mt-1.5 font-semibold navy-text underline" data-testid="impic-agency-link">Preencher em Definições › Agência</Link>
            : <p className="mt-1.5 text-slate-500">Peça à Direção para preencher em Definições › Agência.</p>}
        </div>
      )}
      <div>
        <h3 className="font-semibold text-[15px] navy-text flex items-center gap-2"><FileText size={16} /> Transações ({p.count})</h3>
        {p.count === 0 ? <p className="text-sm text-slate-500 mt-2" data-testid="impic-empty">Sem vendas nem arrendamentos concluídos neste trimestre.</p> : (
          <ul className="divide-y divide-slate-100">{p.rows.map((r) => <TxRow key={r.id} r={r} onEdit={setEdit} />)}</ul>
        )}
      </div>
      {edit && <ImpicDados row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); onChange(); }} />}
      {submit && <ImpicSubmetido p={p} onClose={() => setSubmit(false)} onConfirm={(date) => { setSubmit(false); run(() => api.post(`/impic/periods/${pkey}/submitted`, { date }), "Marcado como submetido"); }} />}
    </section>
  );
}
